import React, { useState } from 'react';
import { Formik, Field } from 'formik';
import { Form as SemanticForm, Grid } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { InputFormField, Text } from '@playbookux/playbook-web-core';
import { useJobsList } from '../../../core/hooks/use-jobs-list';
import SelectFormField from '../../../core/components/select-form-field';
import Categorizing from '../../../core/components/categorizing-select-list';

import { DemographicsSurveySchema } from './schema';
import {
  annualHouseholdIncomeOptions,
  countryOptions,
  genderOptions,
  raceEthnicHeritageOptions,
  stateOptions,
} from '../../pages/sign-up-survey/options';
import { companySize, industry, seniority } from '../../../admin/pages/edit-tester/options';
import { ErrorMessage, TextHeader } from '../../pages/sign-up-survey/styles';
import { SignUpButton, SignUpForm } from './styles';

export const DemographicsSurveyForm = (props) => {
  const {
    onSubmit: handleSubmit,
    loading,
    error,
    audienceAttributes: demographicsOptionFilters,
    isGuest,
  } = props;

  const callbackCatAndSubCat = (items, type, formik) => {
    if (type === 'jobs') {
      formik.setFieldValue('jobsSelected', items);
    }
  };

  const [showError, setShowError] = useState(false);

  const { valuesCatJobs } = useJobsList();

  const originalData = {
    gender: genderOptions,
    ethnicity: raceEthnicHeritageOptions,
    household_income: annualHouseholdIncomeOptions,
    location_state: stateOptions,
    location_country: countryOptions,
    seniority,
    company_size: companySize,
    industry,
  };

  const filteredFormOptions = { ...originalData };

  // This a filter for select components
  // which apply what customers selected in audience segment
  if (isGuest) {
    demographicsOptionFilters.forEach((attribute) => {
      const { audienceAttributeType, audienceAttributeOptions } = attribute;

      if (filteredFormOptions[audienceAttributeType]) {
        if (audienceAttributeType === 'location_country') {
          // for now if there is an option 'other' we shouldn't filter
          if (audienceAttributeOptions.some((option) => option.value === 'other')) {
            return;
          }
          filteredFormOptions[audienceAttributeType] = audienceAttributeOptions.map((option) => ({
            key: option.value,
            value: option.value,
            text: originalData[audienceAttributeType].find((item) => item.value === option.value).text,
            flag: originalData[audienceAttributeType].find((item) => item.value === option.value).flag,
          }));

          return;
        }

        filteredFormOptions[audienceAttributeType] = audienceAttributeOptions.map((option) => ({
          key: option.value,
          value: option.value,
          text: option.value,
        }));
      }
    });
  }

  return (
    <Formik
      initialValues={{
        age: null,
        gender: '',
        raceEthnicHeritage: '',
        annualHouseholdIncome: '',
        companySize: '',
        industry: '',
        seniority: '',
        country: '',
        state: '',
        jobsSelected: [],
      }}
      validationSchema={DemographicsSurveySchema}
      onSubmit={(values) => {
        handleSubmit(values);
      }}
    >
      {(formik) => (
        <SignUpForm>
          <SemanticForm.Field>
            <Grid>
              <Grid.Row>
                <Grid.Column width={4}>
                  <Field
                    name="age"
                    component={InputFormField}
                    label="Age"
                    labelText="Age"
                    labelFontSize="18px"
                    labelColor="#172B4D"
                    type="number"
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </SemanticForm.Field>
          <SemanticForm.Field>
            <Field
              labelFontSize="18px"
              label="Gender"
              name="gender"
              isMulti={false}
              isCreatable={false}
              options={filteredFormOptions.gender}
              labelColor="#172B4D"
              component={SelectFormField}
            />
          </SemanticForm.Field>
          <SemanticForm.Field>
            <Field
              labelFontSize="18px"
              label="What is your ethnicity?"
              name="raceEthnicHeritage"
              isMulti={false}
              isCreatable={false}
              options={filteredFormOptions.ethnicity}
              labelColor="#172B4D"
              component={SelectFormField}
            />
          </SemanticForm.Field>
          <SemanticForm.Field>
            <Field
              labelFontSize="18px"
              label="What is your annual household income in US Dollars?"
              name="annualHouseholdIncome"
              isMulti={false}
              isCreatable={false}
              options={filteredFormOptions.household_income}
              labelColor="#172B4D"
              component={SelectFormField}
            />
          </SemanticForm.Field>
          <SemanticForm.Field>
            <Field
              labelFontSize="18px"
              label="What is your company size?"
              name="companySize"
              isMulti={false}
              isCreatable={false}
              options={filteredFormOptions.company_size}
              labelColor="#172B4D"
              component={SelectFormField}
            />
          </SemanticForm.Field>
          <SemanticForm.Field>
            <Field
              labelFontSize="18px"
              label="What industry does your company operate in?"
              name="industry"
              isMulti={false}
              isCreatable={false}
              options={filteredFormOptions.industry}
              labelColor="#172B4D"
              component={SelectFormField}
            />
          </SemanticForm.Field>
          <SemanticForm.Field>
            <Field
              labelFontSize="18px"
              label="What is your seniority?"
              name="seniority"
              isMulti={false}
              isCreatable={false}
              options={filteredFormOptions.seniority}
              labelColor="#172B4D"
              component={SelectFormField}
            />
          </SemanticForm.Field>
          <SemanticForm.Field>
            <Field
              label="Country"
              name="country"
              placeholder="Select"
              isMulti={false}
              isCreatable={false}
              options={filteredFormOptions.location_country}
              labelFontSize="18px"
              labelColor="#172B4D"
              component={SelectFormField}
            />
          </SemanticForm.Field>
          {formik.values.country === 'us' && (
            <SemanticForm.Field>
              <Field
                label="State"
                name="state"
                placeholder="Select"
                isMulti={false}
                isCreatable={false}
                options={filteredFormOptions.location_state}
                labelFontSize="18px"
                labelColor="#172B4D"
                component={SelectFormField}
              />
            </SemanticForm.Field>
          )}

          <SemanticForm.Field>
            <TextHeader>
              Job Roles
            </TextHeader>
            <Text>
              Which of the below role(s) resembles your job most?
            </Text>
          </SemanticForm.Field>
          <Categorizing
            category={valuesCatJobs}
            callbackCatAndSubCat={(items, type) => callbackCatAndSubCat(items, type, formik)}
            type="jobs"
            values={formik.values.jobsSelected}
            error={showError && formik.errors.jobsSelected ? 'Job roles is required' : null}
            isRadioBtn
          />

          <SemanticForm.Field>
            <Grid>
              <Grid.Row centered>
                <Grid.Column verticalAlign="center">
                  {
                    Object.values(formik.errors).some((e) => e) && showError && (
                      <ErrorMessage>
                        Please enter a
                        {
                          `${Object.keys(formik.errors).map((key) => {
                            if (formik.touched[key] && formik.errors[key]) {
                              return (
                                ` ${formik.errors[key]}`
                              );
                            }
                            return null;
                          })}`
                        }
                      </ErrorMessage>
                    )
                  }
                  {error && <ErrorMessage>{error.message}</ErrorMessage>}
                  <SignUpButton type="submit" onClick={() => setShowError(true)} loading={loading}>Next</SignUpButton>
                </Grid.Column>
              </Grid.Row>
            </Grid>

          </SemanticForm.Field>

        </SignUpForm>

      )}
    </Formik>
  );
};

const AudienceAttributeOptionType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  flag: PropTypes.string,
  text: PropTypes.string,
});

const AudienceAttributeType = PropTypes.shape({
  audienceAttributeType: PropTypes.string.isRequired,
  audienceAttributeOptions: PropTypes.arrayOf(AudienceAttributeOptionType).isRequired,
});

DemographicsSurveyForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.shape({
    message: PropTypes.string,
  }),
  audienceAttributes: PropTypes.arrayOf(AudienceAttributeType).isRequired,
  isGuest: PropTypes.bool.isRequired,
};

DemographicsSurveyForm.defaultProps = {
  loading: false,
  error: null,
};

export default DemographicsSurveyForm;
